import { useMemo } from 'react';
import { useRegionalConfigContext } from '@/contexts/RegionalConfigContext';
import {
  formatPrice as formatPriceFn,
  getLocaleFromCountry,
  DEFAULT_CURRENCY,
  DEFAULT_LOCALE,
} from '@/lib/formatting/currency';

/**
 * Return type for useCurrency hook
 * Requirements: 1.1, 1.3, 1.4, 1.5
 */
export interface UseCurrencyReturn {
  /** Default currency from system configuration */
  currency: string;
  /** Locale derived from system country configuration */
  locale: string;
  /** Function to format prices with system defaults */
  formatPrice: (amount: number, overrideCurrency?: string, options?: { showCode?: boolean, overrideLocale?: string, fractionDigits?: number }) => string;
  /** Indicates if configuration is ready */
  isReady: boolean;
}

/**
 * Hook for currency formatting based on regional configuration
 * 
 * Provides currency, locale, and formatPrice function that automatically
 * use the system's regional configuration. Falls back to safe defaults
 * (MXN, es-MX) if configuration is not available.
 * 
 * @returns Currency formatting utilities and configuration
 * 
 * @example
 * const { currency, formatPrice } = useCurrency();
 * const formatted = formatPrice(1299.99); // Uses system currency
 * const formattedUSD = formatPrice(1299.99, 'USD'); // Override currency
 * 
 * Requirements: 1.1, 1.3, 1.4, 1.5, 9.1-9.4
 */
export function useCurrency(): UseCurrencyReturn {
  try {
    const context = useRegionalConfigContext();
    
    // Ensure we always have valid values, even if context is loading
    const currency = context?.defaultCurrency || DEFAULT_CURRENCY;
    const locale = useMemo(
      () => {
        if (!context?.defaultCountry) return DEFAULT_LOCALE;
        return getLocaleFromCountry(context.defaultCountry);
      },
      [context?.defaultCountry]
    );
    
    // Memoize formatPrice function to avoid recreating on every render
    const formatPrice = useMemo(
      () => (amount: number, overrideCurrency?: string, options?: { showCode?: boolean, overrideLocale?: string, fractionDigits?: number }) => {
        try {
          const effectiveCurrency = overrideCurrency || currency;
          const effectiveLocale = options?.overrideLocale || locale;
          return formatPriceFn(amount, effectiveCurrency, effectiveLocale, options);
        } catch (error) {
          console.error('Error formatting price in useCurrency:', error);
          // Fallback with currency symbol
          return formatPriceFn(amount, overrideCurrency || currency, options?.overrideLocale || DEFAULT_LOCALE, options);
        }
      },
      [currency, locale]
    );
    
    return {
      currency,
      locale,
      formatPrice,
      isReady: !!context?.defaultCurrency,
    };
  } catch (error) {
    console.error('Error in useCurrency hook:', error);
    
    // Fallback to safe defaults
    return {
      currency: DEFAULT_CURRENCY,
      locale: DEFAULT_LOCALE,
      formatPrice: (amount: number, overrideCurrency?: string, options?: { showCode?: boolean, overrideLocale?: string, fractionDigits?: number }) =>
        formatPriceFn(amount, overrideCurrency || DEFAULT_CURRENCY, options?.overrideLocale || DEFAULT_LOCALE, options),
      isReady: false,
    };
  }
}
